import React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";

import Graph from "src/library/components/Graph";
import {
  TotalTicketsByTypeResult,
  WidgetSlug,
  useWidgetDataApi
} from "src/core/dashboard/api/widget-data.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { GraphPayload } from "src/library/components/Graph/GraphToolTip";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { WidgetDisplayTypes } from "src/core/dashboard/api/dashboard.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import {
  getDisplayRange,
  getGraphMetrics,
  DisplayRangeType,
  getSelectedWidgetView,
  WidgetProps
} from "../utils";
import DataLoadError from "../DataLoadError";
import { groupTotalTicketsByType } from "./utils";
import { WidgetInnerHeader } from "../WidgetInnerHeader";
import { NumberWidget } from "../NumberWidget";
import { useWidgetFontSize } from "../hooks";

// Type for the flat object used to display data in graph
export interface TotalTicketsByTypeGraphValue extends Omit<GraphPayload, "date"> {
  /** xAxisBy date */
  date?: Date;
  /** xAxisBy type */
  type?: string;
  count?: number;
}

// TotalTicketsByTypeGraphValueType tells us what data is passed to display in a graph
// We use Record<string, number> as the type is equal to the name of the support ticket type table,
// which is not an enum or known strict value.
export type TotalTicketsByTypeGraphValueType = TotalTicketsByTypeGraphValue & Record<string, number>;

const SUPPORT_TOTAL_TICKETS_BY_TYPE_CACHE_KEY = "widget_support_total_tickets_by_type";

/**
 * Returns total tickets by status widget content
 */
export const TotalTicketsByTypeWidget: React.FunctionComponent<WidgetProps> = ({
  options, filters, widgetId, widgetTypeId, ignoreCache = false
}) => {
  const [ displayRange, setDisplayRange ] = React.useState<DisplayRangeType>("day");
  const [ totalTicketsByTypeResponse, totalTicketsByTypeRequest ] = useWidgetDataApi("GET_TOTAL_TICKETS_BY_TYPE");
  const [ xAxisBy, setXAisBy ] = React.useState<"date" | "type">("date");
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { width } = useWidgetContainerSize(wrapperRef);
  const fontSize = useWidgetFontSize(width, options.view);

  const selectedView = React.useMemo(() => {
    return getSelectedWidgetView(options.view);
  }, [ options ]);

  // On load, get the data we need
  React.useEffect(() => {
    totalTicketsByTypeRequest({
      widgetTypeId,
      filters,
      options: null
    }, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => `${SUPPORT_TOTAL_TICKETS_BY_TYPE_CACHE_KEY}-${widgetTypeId}`,
        ignoreCache
      }
    });
  }, [
    filters,
    ignoreCache,
    totalTicketsByTypeRequest,
    widgetTypeId
  ]);

  // Takes the raw response from API and transforms the result to the shape required for graph data.
  // During this process, we also sets the displayRange & xAxisBy state
  const transformToGraphData = React.useCallback((totalTicketsByTypeResult: TotalTicketsByTypeResult): TotalTicketsByTypeGraphValueType[] => {
    // Find range to determine whether to display in days, weeks or months
    const calculatedDisplayRange = getDisplayRange(totalTicketsByTypeResult);

    // Update state required by Graph component
    setDisplayRange(calculatedDisplayRange);
    setXAisBy(calculatedDisplayRange === "day" ? "type" : "date");

    // Group, accumulate totals and return
    return groupTotalTicketsByType(totalTicketsByTypeResult, xAxisBy, calculatedDisplayRange);
  }, [ xAxisBy ]);

  // The data used to display metrics
  const graphDisplayData = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      if (totalTicketsByTypeResponse.data?.result) {
        return transformToGraphData(totalTicketsByTypeResponse.data?.result);
      }
    }

    return null;
  }, [
    selectedView,
    totalTicketsByTypeResponse.data?.result,
    transformToGraphData
  ]);

  const graphMetrics = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      return graphDisplayData ? getGraphMetrics(graphDisplayData, [ xAxisBy ]) : [];
    }

    return null;
  }, [
    graphDisplayData,
    selectedView,
    xAxisBy
  ]);

  // The data used to display number metrics
  const numberDisplayData = React.useMemo(() => {
    if (selectedView === WidgetDisplayTypes.Number) {
      const totalTicketsByTypeResult = totalTicketsByTypeResponse.data?.result;

      if (totalTicketsByTypeResult) {
        const totalTicketsByType = groupTotalTicketsByType(totalTicketsByTypeResult, "type") as Array<{ type: string, count: number }>;

        return {
          value: totalTicketsByTypeResult.reduce((total, ticket) => total + ticket.count, 0),
          data: totalTicketsByType.map(typeData => {
            return {
              group: _.startCase(typeData.type.replace(/[_-]/g, " ")),
              value: typeData.count
            };
          })
        };
      }
    }

    return null;
  }, [ totalTicketsByTypeResponse.data?.result, selectedView ]);

  if (selectedView !== "bar" && selectedView !== "line" && selectedView !== WidgetDisplayTypes.Number) {
    // Currently, graph bar and line are the only handled view options availble below
    return null;
  }

  return (
    <StyledWrapper ref={wrapperRef}>
      {totalTicketsByTypeResponse.pending && (
        <LoadingSpinner type="circular" />
      )}

      {graphDisplayData && graphMetrics && !totalTicketsByTypeResponse.error && (selectedView === "line" || selectedView === "bar") && (
        <>
          <WidgetInnerHeader title="Total Tickets" />

          <Graph
            containerWidth={width && width}
            hideLegend={xAxisBy === "type"}
            stacked={xAxisBy === "date"}
            data={graphDisplayData}
            type={selectedView}
            toolTipValueType="number"
            graphMetrics={graphMetrics}
            xAxis={{
              dataKey: xAxisBy,
              dateFormat: displayRange === "month" ? "month" : "day"
            }}
          />
        </>
      )}

      {numberDisplayData && fontSize && !totalTicketsByTypeResponse.error && (
        <NumberWidget
          widgetId={widgetId}
          displayData={numberDisplayData}
          valueType="number"
          units="tickets"
          fontSize={fontSize}
          hideToolTip
        />
      )}

      {totalTicketsByTypeResponse.error && (
        <DataLoadError errorMessage={totalTicketsByTypeResponse.error.message} />
      )}

    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    width: 100%;
    padding: ${theme.spacing.padding.sm} 0; 
  `}
`;
